<script setup>
import { computed, nextTick, onMounted, reactive, ref, watch } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useI18n } from 'vue-i18n';
import Modal from 'dashboard/components/Modal.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import DeleteModal from 'dashboard/components/widgets/modal/DeleteModal.vue';
import { useProductSpecifications } from './composables/useProductSpecifications';
import {
  convertImageFileToWebP,
  uploadFileToSignedUrl,
} from './helpers/imageUpload';

const store = useStore();
const { t } = useI18n();
const { isAdmin } = useAdmin();

const query = ref('');
const statusFilter = ref('');
const categoryFilter = ref('');
const isProductModalOpen = ref(false);
const isSummaryModalOpen = ref(false);
const isDeleteModalOpen = ref(false);
const isSettingsModalOpen = ref(false);
const selectedProduct = ref(null);
const pendingDeleteProduct = ref(null);
const imageFileInputRef = ref(null);
const isUploadingImages = ref(false);
const sheetsWebhookUrl = ref('');
const isSavingWebhook = ref(false);

const productDraft = reactive({
  name: '',
  status: 'active',
  category_public_id: '',
  sale_price: 0,
  cost_price: 0,
  description: '',
});

const storeProducts = computed(() => store.getters['products/getProducts']);
const products = computed(() => storeProducts.value || []);
const categories = computed(
  () => store.getters['products/getCategories'] || []
);
const customFields = computed(
  () => store.getters['products/getCustomFields'] || []
);
const specificationPresets = computed(
  () => store.getters['products/getSpecificationPresets'] || []
);
const uiFlags = computed(() => store.getters['products/getUIFlags']);

const {
  drafts: customFieldDrafts,
  addField: addCustomField,
  removeField: removeCustomField,
  applyPreset,
  hydrate: hydrateSpecifications,
  serialize: serializeSpecifications,
  getPersistedFieldKey,
} = useProductSpecifications();

const visibleProducts = computed(() => products.value.length);
const activeProducts = computed(
  () => products.value.filter(p => p.attributes?.status === 'active').length
);
const averageTicket = computed(() => {
  if (!products.value.length) return 0;
  const sum = products.value.reduce(
    (acc, p) => acc + Number(p.attributes?.sale_price_cents || 0),
    0
  );
  return Math.round(sum / products.value.length);
});

const hasExistingImages = computed(
  () => (selectedProduct.value?.attributes?.images || []).length > 0
);

const toReusableFieldDraft = field => ({
  custom_field_public_id: field.id,
  name: field.attributes?.name,
  slug: field.attributes?.slug,
  field_type: field.attributes?.field_type,
  required: field.attributes?.required,
});

const hasReusableFieldMatch = field => {
  const reusableDraft = toReusableFieldDraft(field);
  const reusableFieldId = reusableDraft.custom_field_public_id?.trim();
  const reusableLogicalKey = getPersistedFieldKey({
    name: reusableDraft.name,
    slug: reusableDraft.slug,
  });

  return customFieldDrafts.value.some(draftField => {
    if (
      reusableFieldId &&
      draftField.custom_field_public_id?.trim() === reusableFieldId
    )
      return true;
    if (!reusableLogicalKey) return false;
    return (
      getPersistedFieldKey({ name: draftField.name, slug: draftField.slug }) ===
      reusableLogicalKey
    );
  });
};

const availableReusableFields = computed(() =>
  customFields.value.filter(field => !hasReusableFieldMatch(field))
);

const productStatusOptions = ['active', 'inactive', 'out_of_stock'];
const customFieldTypeOptions = [
  'text',
  'number',
  'boolean',
  'select',
  'multiselect',
  'date',
];

const formatMoney = cents =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
    Number(cents || 0) / 100
  );

const numberOrZero = value => Number(value || 0);
const toCents = value => Math.round(numberOrZero(value) * 100);
const fromCents = value => numberOrZero(value) / 100;

const profitPreview = computed(() => {
  if (!isAdmin.value) return null;
  return toCents(productDraft.sale_price) - toCents(productDraft.cost_price);
});

const profitMarginPreview = computed(() => {
  if (!isAdmin.value) return null;
  const sale = numberOrZero(productDraft.sale_price);
  if (sale <= 0) return 0;
  return (
    ((sale - numberOrZero(productDraft.cost_price)) / sale) *
    100
  ).toFixed(1);
});

const fetchProducts = () =>
  store.dispatch('products/getProducts', {
    query: query.value || undefined,
    status: statusFilter.value || undefined,
    category_id: categoryFilter.value || undefined,
  });

const resetDraft = () => {
  Object.assign(productDraft, {
    name: '',
    status: 'active',
    category_public_id: '',
    sale_price: 0,
    cost_price: 0,
    description: '',
  });
  hydrateSpecifications([]);
};

const clearProductModalState = () => {
  selectedProduct.value = null;
  resetDraft();
};

const openCreateProductModal = () => {
  clearProductModalState();
  isProductModalOpen.value = true;
};

const loadProductDetail = async publicId => {
  const product = await store.dispatch('products/getProduct', publicId);
  selectedProduct.value = product;
  return product;
};

const openEditProductModal = async product => {
  try {
    const detailed = await loadProductDetail(product.id);
    Object.assign(productDraft, {
      name: detailed.attributes?.name || '',
      status: detailed.attributes?.status || 'active',
      category_public_id: detailed.attributes?.category_public_id || '',
      sale_price: fromCents(detailed.attributes?.sale_price_cents),
      cost_price: fromCents(detailed.attributes?.cost_price_cents),
      description: detailed.attributes?.description || '',
    });
    hydrateSpecifications(detailed.attributes?.custom_fields || []);
    isProductModalOpen.value = true;
  } catch (error) {
    useAlert(error?.response?.data?.error || t('PRODUCTS.LOAD_PRODUCTS'));
  }
};

const closeProductModal = () => {
  isProductModalOpen.value = false;
};

const openImageFilePicker = () => {
  if (isUploadingImages.value) return;
  imageFileInputRef.value?.click();
};

const buildProductPayload = () => {
  const payload = {
    name: productDraft.name,
    status: productDraft.status,
    description: productDraft.description,
    sale_price_cents: toCents(productDraft.sale_price),
    base_price_cents: toCents(productDraft.sale_price),
    category_public_id: productDraft.category_public_id ?? '',
    custom_field_values: serializeSpecifications(),
  };

  if (isAdmin.value) {
    payload.cost_price_cents = toCents(productDraft.cost_price);
  }

  return payload;
};

const persistProductDraft = async ({
  notify = true,
  closeOnSuccess = false,
} = {}) => {
  const payload = buildProductPayload();

  if (selectedProduct.value?.id) {
    const updated = await store.dispatch('products/updateProduct', {
      publicId: selectedProduct.value.id,
      product: payload,
    });
    selectedProduct.value = updated;
    if (notify) useAlert(t('PRODUCTS.UPDATE_SUCCESS'));
    if (closeOnSuccess) closeProductModal();
    return updated;
  }

  const created = await store.dispatch('products/createProduct', payload);
  selectedProduct.value = created;
  if (notify) useAlert(t('PRODUCTS.CREATE_SUCCESS'));
  if (closeOnSuccess) closeProductModal();
  return created;
};

const uploadSelectedImages = async files => {
  if (!files.length) return;
  if (!selectedProduct.value?.id && !productDraft.name.trim()) {
    useAlert(t('PRODUCTS.NAME_REQUIRED'));
    return;
  }
  isUploadingImages.value = true;
  try {
    const product = await persistProductDraft({ notify: false });
    const existingCount =
      selectedProduct.value?.attributes?.images?.length || 0;

    await files.reduce(async (chain, file, index) => {
      await chain;
      const webpFile = await convertImageFileToWebP(file);
      const uploadPayload = await store.dispatch(
        'products/requestImageUpload',
        {
          publicId: product.id,
          image: {
            alt_text: productDraft.name || file.name,
            position: existingCount + index,
            is_primary: existingCount + index === 0,
          },
          upload: { filename: webpFile.name, content_type: webpFile.type },
        }
      );
      await uploadFileToSignedUrl(uploadPayload.upload_url, webpFile);
    }, Promise.resolve());

    await fetchProducts();
    await loadProductDetail(product.id);
  } finally {
    isUploadingImages.value = false;
  }
};

const deleteImage = async image => {
  try {
    await store.dispatch('products/deleteImage', {
      productPublicId: selectedProduct.value.id,
      imagePublicId: image.public_id,
    });
    await loadProductDetail(selectedProduct.value.id);
    await fetchProducts();
  } catch (error) {
    useAlert(error?.response?.data?.error || t('PRODUCTS.SAVE_ERROR'));
  }
};

const onImageFilesSelected = async event => {
  const files = Array.from(event.target.files || []);
  event.target.value = '';
  if (!files.length) return;
  try {
    await uploadSelectedImages(files);
  } catch (error) {
    useAlert(
      error?.response?.data?.error || error?.message || t('PRODUCTS.SAVE_ERROR')
    );
  }
};

const submitProduct = async () => {
  try {
    await persistProductDraft({ closeOnSuccess: true });
  } catch (error) {
    useAlert(error?.response?.data?.error || t('PRODUCTS.SAVE_ERROR'));
  }
};

const promptDeleteProduct = product => {
  pendingDeleteProduct.value = product;
  isDeleteModalOpen.value = true;
};

const closeDeleteModal = () => {
  isDeleteModalOpen.value = false;
  pendingDeleteProduct.value = null;
};

const confirmDeleteProduct = async () => {
  if (!pendingDeleteProduct.value?.id) return;
  try {
    await store.dispatch(
      'products/deleteProduct',
      pendingDeleteProduct.value.id
    );
    useAlert(t('PRODUCTS.DELETE_SUCCESS'));
    closeDeleteModal();
    if (selectedProduct.value?.id === pendingDeleteProduct.value.id)
      closeProductModal();
  } catch (error) {
    useAlert(error?.response?.data?.error || t('PRODUCTS.DELETE_ERROR'));
  }
};

const saveWebhookUrl = async () => {
  isSavingWebhook.value = true;
  try {
    await store.dispatch('products/updateSettings', {
      sheetsWebhookUrl: sheetsWebhookUrl.value,
    });
    useAlert(t('PRODUCTS.SHEETS_SAVED'));
  } catch (error) {
    useAlert(t('PRODUCTS.SHEETS_SAVE_ERROR'));
  } finally {
    isSavingWebhook.value = false;
  }
};

watch([query, statusFilter, categoryFilter], fetchProducts);

watch(isProductModalOpen, isOpen => {
  if (isOpen) return;
  nextTick(() => {
    if (!isProductModalOpen.value) clearProductModalState();
  });
});

onMounted(async () => {
  try {
    await Promise.all([
      fetchProducts(),
      store.dispatch('products/getCategories'),
      store.dispatch('products/getCustomFields'),
      store.dispatch('products/getSpecificationPresets'),
    ]);
    const settings = await store.dispatch('products/getSettings');
    sheetsWebhookUrl.value = settings?.sheets_webhook_url || '';
  } catch {
    // settings endpoint may not be configured yet — non-fatal
  }
});
</script>

<template>
  <div
    class="flex h-full w-full min-w-0 flex-1 flex-col overflow-hidden bg-n-surface-1"
  >
    <!-- Header -->
    <div class="border-b border-n-weak px-6 py-4">
      <div class="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 class="text-xl font-semibold text-n-slate-12">
            {{ t('PRODUCTS.TITLE') }}
          </h1>
        </div>
        <div class="flex items-center gap-2">
          <Button
            color="slate"
            size="sm"
            variant="faded"
            icon="i-lucide-settings-2"
            @click="isSettingsModalOpen = true"
          />
          <Button
            color="slate"
            size="sm"
            variant="outline"
            :label="t('PRODUCTS.VIEW_SUMMARY')"
            @click="isSummaryModalOpen = true"
          />
          <Button
            color="blue"
            size="sm"
            :label="t('PRODUCTS.NEW_PRODUCT')"
            @click="openCreateProductModal"
          />
        </div>
      </div>

      <div
        class="mt-5 grid gap-3 md:grid-cols-2 2xl:grid-cols-[minmax(0,1.2fr)_15rem_18rem]"
      >
        <input
          v-model="query"
          type="search"
          :placeholder="t('PRODUCTS.SEARCH_PLACEHOLDER')"
          class="w-full rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2 text-sm text-n-slate-12"
        />

        <select
          v-model="statusFilter"
          class="w-full rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2 text-sm text-n-slate-12"
        >
          <option value="">{{ t('PRODUCTS.ALL_STATUSES') }}</option>
          <option
            v-for="status in productStatusOptions"
            :key="status"
            :value="status"
          >
            {{ t(`PRODUCTS.STATUS.${status}`) }}
          </option>
        </select>

        <select
          v-model="categoryFilter"
          class="w-full rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2 text-sm text-n-slate-12"
        >
          <option value="">{{ t('PRODUCTS.ALL_CATEGORIES') }}</option>
          <option
            v-for="category in categories"
            :key="category.id"
            :value="category.id"
          >
            {{ category.attributes?.name }}
          </option>
        </select>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="uiFlags.isFetching" class="p-6 text-n-slate-11">
      {{ t('PRODUCTS.LOADING') }}
    </div>

    <!-- Empty state -->
    <div v-else-if="!products.length" class="flex-1 overflow-auto p-6">
      <div class="flex min-h-full">
        <div
          class="flex min-h-[26rem] w-full flex-col justify-between rounded-3xl border border-dashed border-n-weak bg-n-alpha-1 p-8 text-n-slate-11"
        >
          <div class="max-w-3xl">
            <h2 class="text-2xl font-semibold text-n-slate-12 sm:text-3xl">
              {{ t('PRODUCTS.EMPTY_TITLE') }}
            </h2>
            <p class="mt-3 max-w-2xl text-base leading-7">
              {{ t('PRODUCTS.EMPTY_DESCRIPTION') }}
            </p>
            <div class="mt-8 flex flex-wrap gap-3">
              <Button
                color="blue"
                :label="t('PRODUCTS.NEW_PRODUCT')"
                @click="openCreateProductModal"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Product list -->
    <div v-else class="flex-1 overflow-auto">
      <button
        v-for="product in products"
        :key="product.id"
        type="button"
        class="flex w-full items-center gap-4 border-b border-n-weak px-6 py-3 text-left transition hover:bg-n-alpha-1"
        @click="openEditProductModal(product)"
      >
        <!-- Thumbnail -->
        <div class="shrink-0">
          <img
            v-if="product.attributes?.primary_image_url"
            :src="product.attributes.primary_image_url"
            :alt="product.attributes?.name"
            class="h-14 w-14 rounded-xl border border-n-weak object-cover"
          />
          <div
            v-else
            class="h-14 w-14 rounded-xl border border-dashed border-n-weak bg-n-alpha-2"
          />
        </div>

        <!-- Name + category -->
        <div class="min-w-0 flex-1">
          <div class="flex items-center gap-2">
            <span class="truncate font-medium text-n-slate-12">
              {{ product.attributes?.name }}
            </span>
            <span
              class="shrink-0 rounded-full bg-n-alpha-3 px-2 py-0.5 text-xs text-n-slate-11"
            >
              {{ t(`PRODUCTS.STATUS.${product.attributes?.status}`) }}
            </span>
          </div>
          <p class="mt-0.5 text-xs text-n-slate-11">
            {{ product.attributes?.category_name || t('PRODUCTS.NO_CATEGORY') }}
          </p>
        </div>

        <!-- Pricing -->
        <div class="shrink-0 text-right">
          <p class="font-semibold text-n-slate-12">
            {{ formatMoney(product.attributes?.sale_price_cents) }}
          </p>
          <p
            v-if="isAdmin && product.attributes?.profit_cents != null"
            class="mt-0.5 text-xs text-n-slate-10"
          >
            {{ t('PRODUCTS.FORM.PROFIT') }}:
            {{ formatMoney(product.attributes.profit_cents) }}
            <span
              v-if="product.attributes?.profit_margin_percentage"
              class="ml-1"
            >
              ({{ product.attributes.profit_margin_percentage }}%)
            </span>
          </p>
        </div>
      </button>
    </div>

    <!-- Product modal -->
    <Modal
      v-model:show="isProductModalOpen"
      full-width
      :show-close-button="false"
      @close="closeProductModal"
    >
      <div class="w-full max-w-[72rem] overflow-x-hidden p-6">
        <!-- Modal header -->
        <div class="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 class="text-lg font-semibold text-n-slate-12">
              {{
                selectedProduct
                  ? t('PRODUCTS.EDIT_PRODUCT')
                  : t('PRODUCTS.NEW_PRODUCT')
              }}
            </h2>
            <p class="mt-1 text-sm text-n-slate-11">
              {{ t('PRODUCTS.EDIT_HINT') }}
            </p>
          </div>

          <div class="flex flex-wrap items-center gap-2">
            <Button
              v-if="selectedProduct"
              color="ruby"
              variant="outline"
              size="sm"
              :label="t('PRODUCTS.DELETE_PRODUCT')"
              @click="promptDeleteProduct(selectedProduct)"
            />
            <Button
              ghost
              slate
              icon="i-lucide-x"
              size="sm"
              @mousedown.stop.prevent
              @click.stop.prevent="closeProductModal"
            />
          </div>
        </div>

        <!-- Two-column layout: form left, images right -->
        <div class="mt-6 grid gap-6 xl:grid-cols-[minmax(0,1fr)_22rem]">
          <!-- LEFT: form -->
          <form
            class="max-h-[74vh] space-y-5 overflow-y-auto pr-1 pb-4"
            @submit.prevent="submitProduct"
          >
            <!-- Basic info -->
            <section
              class="space-y-4 rounded-3xl border border-n-weak bg-n-alpha-1 p-5"
            >
              <div>
                <label class="mb-1 block text-sm font-medium text-n-slate-11">
                  {{ t('PRODUCTS.FORM.NAME') }}
                </label>
                <input
                  v-model="productDraft.name"
                  class="w-full rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2"
                  required
                />
              </div>

              <div class="grid gap-4 md:grid-cols-2">
                <div>
                  <label class="mb-1 block text-sm font-medium text-n-slate-11">
                    {{ t('PRODUCTS.FORM.CATEGORY') }}
                  </label>
                  <select
                    v-model="productDraft.category_public_id"
                    class="w-full rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2"
                  >
                    <option value="">{{ t('PRODUCTS.NO_CATEGORY') }}</option>
                    <option
                      v-for="category in categories"
                      :key="category.id"
                      :value="category.id"
                    >
                      {{ category.attributes?.name }}
                    </option>
                  </select>
                </div>

                <div>
                  <label class="mb-1 block text-sm font-medium text-n-slate-11">
                    {{ t('PRODUCTS.FORM.STATUS') }}
                  </label>
                  <select
                    v-model="productDraft.status"
                    class="w-full rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2"
                  >
                    <option
                      v-for="status in productStatusOptions"
                      :key="status"
                      :value="status"
                    >
                      {{ t(`PRODUCTS.STATUS.${status}`) }}
                    </option>
                  </select>
                </div>
              </div>

              <div>
                <label class="mb-1 block text-sm font-medium text-n-slate-11">
                  {{ t('PRODUCTS.FORM.DESCRIPTION') }}
                </label>
                <textarea
                  v-model="productDraft.description"
                  rows="3"
                  class="w-full rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2 text-sm"
                />
              </div>
            </section>

            <!-- Pricing -->
            <section class="rounded-3xl border border-n-weak bg-n-alpha-1 p-5">
              <h3 class="text-base font-semibold text-n-slate-12">
                {{ t('PRODUCTS.FORM.SELLING_PANEL') }}
              </h3>

              <div class="mt-4 grid gap-4 md:grid-cols-2">
                <div v-if="isAdmin">
                  <label class="mb-1 block text-sm font-medium text-n-slate-11">
                    {{ t('PRODUCTS.FORM.COST_PRICE') }}
                  </label>
                  <input
                    v-model.number="productDraft.cost_price"
                    type="number"
                    min="0"
                    step="0.01"
                    class="w-full rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2"
                  />
                </div>

                <div>
                  <label class="mb-1 block text-sm font-medium text-n-slate-11">
                    {{ t('PRODUCTS.FORM.SALE_PRICE') }}
                  </label>
                  <input
                    v-model.number="productDraft.sale_price"
                    type="number"
                    min="0"
                    step="0.01"
                    class="w-full rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2"
                  />
                </div>
              </div>

              <div
                v-if="isAdmin"
                class="mt-4 grid gap-3 rounded-2xl bg-n-surface-1 px-4 py-4 sm:grid-cols-2"
              >
                <div>
                  <p
                    class="text-xs uppercase tracking-[0.12em] text-n-slate-10"
                  >
                    {{ t('PRODUCTS.FORM.PROFIT') }}
                  </p>
                  <p class="mt-1 text-base font-semibold text-n-slate-12">
                    {{ formatMoney(profitPreview) }}
                  </p>
                </div>
                <div>
                  <p
                    class="text-xs uppercase tracking-[0.12em] text-n-slate-10"
                  >
                    {{ t('PRODUCTS.FORM.MARGIN') }}
                  </p>
                  <p class="mt-1 text-base font-semibold text-n-slate-12">
                    {{ profitMarginPreview }}%
                  </p>
                </div>
              </div>
            </section>

            <!-- Specifications -->
            <section class="rounded-3xl border border-n-weak bg-n-alpha-1 p-5">
              <div class="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h3 class="text-base font-semibold text-n-slate-12">
                    {{ t('PRODUCTS.FORM.SPECIFICATIONS') }}
                  </h3>
                  <p class="mt-1 text-sm text-n-slate-11">
                    {{ t('PRODUCTS.FORM.SPECIFICATIONS_HINT') }}
                  </p>
                </div>
                <button
                  type="button"
                  class="inline-flex items-center justify-center rounded-lg border border-n-weak px-3 py-2 text-sm font-medium text-n-slate-11 transition hover:bg-n-alpha-2"
                  @click.prevent.stop="addCustomField()"
                >
                  {{ t('PRODUCTS.FORM.ADD_CUSTOM_FIELD') }}
                </button>
              </div>

              <div class="mt-4 flex flex-wrap gap-2">
                <button
                  v-for="preset in specificationPresets"
                  :key="preset.key"
                  type="button"
                  class="rounded-full border border-n-weak bg-n-surface-1 px-3 py-2 text-sm text-n-slate-11 transition hover:border-n-slate-6 hover:text-n-slate-12"
                  @click.prevent.stop="applyPreset(preset)"
                >
                  {{ preset.name }}
                </button>
              </div>

              <div
                v-if="availableReusableFields.length"
                class="mt-4 flex flex-wrap gap-2"
              >
                <button
                  v-for="field in availableReusableFields.slice(0, 6)"
                  :key="field.id"
                  type="button"
                  class="rounded-full border border-n-weak bg-n-alpha-2 px-3 py-2 text-xs text-n-slate-11 transition hover:border-n-slate-6 hover:text-n-slate-12"
                  @click="addCustomField(toReusableFieldDraft(field))"
                >
                  + {{ field.attributes?.name }}
                </button>
              </div>

              <div class="mt-5 space-y-3">
                <div
                  v-for="(field, index) in customFieldDrafts"
                  :key="`${field.custom_field_public_id || 'new'}-${index}`"
                  class="grid gap-3 rounded-2xl border border-n-weak bg-n-surface-1 p-4 md:grid-cols-[minmax(0,1fr)_9rem_minmax(0,1fr)_auto]"
                >
                  <input
                    v-model="field.name"
                    :placeholder="t('PRODUCTS.FORM.FIELD_NAME')"
                    class="w-full rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2"
                  />
                  <select
                    v-model="field.field_type"
                    class="w-full rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2"
                  >
                    <option
                      v-for="ft in customFieldTypeOptions"
                      :key="ft"
                      :value="ft"
                    >
                      {{ t(`PRODUCTS.FIELD_TYPES.${ft}`) }}
                    </option>
                  </select>
                  <input
                    v-model="field.value"
                    :placeholder="t('PRODUCTS.FORM.FIELD_VALUE')"
                    class="w-full rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2"
                  />
                  <Button
                    color="ruby"
                    variant="outline"
                    size="sm"
                    :label="t('PRODUCTS.REMOVE')"
                    @click.prevent="removeCustomField(index)"
                  />
                </div>

                <div
                  v-if="!customFieldDrafts.length"
                  class="rounded-2xl border border-dashed border-n-weak bg-n-surface-1 px-4 py-5 text-sm text-n-slate-11"
                >
                  {{ t('PRODUCTS.FORM.NO_SPECIFICATIONS') }}
                </div>
              </div>
            </section>

            <!-- Sticky footer -->
            <div
              class="sticky bottom-0 z-10 rounded-2xl border border-n-weak bg-n-alpha-1/95 px-4 py-4 shadow-sm backdrop-blur-sm"
            >
              <div class="flex flex-wrap justify-end gap-3">
                <Button
                  type="button"
                  color="slate"
                  variant="outline"
                  :label="t('PRODUCTS.CANCEL')"
                  @click.prevent="closeProductModal"
                />
                <Button
                  color="blue"
                  :label="
                    selectedProduct
                      ? t('PRODUCTS.SAVE_PRODUCT')
                      : t('PRODUCTS.CREATE_PRODUCT')
                  "
                  @click.prevent="submitProduct"
                />
              </div>
            </div>
          </form>

          <!-- RIGHT: image gallery -->
          <div class="flex flex-col gap-4">
            <div class="rounded-3xl border border-n-weak bg-n-alpha-1 p-5">
              <div class="flex items-center justify-between gap-3">
                <h3 class="text-base font-semibold text-n-slate-12">
                  {{ t('PRODUCTS.FORM.IMAGES') }}
                </h3>
                <Button
                  type="button"
                  color="blue"
                  size="sm"
                  :label="t('PRODUCTS.FORM.UPLOAD_IMAGES')"
                  :disabled="isUploadingImages"
                  @click.prevent="openImageFilePicker"
                />
              </div>

              <input
                ref="imageFileInputRef"
                type="file"
                accept="image/*"
                multiple
                class="hidden"
                @change="onImageFilesSelected"
              />

              <div
                v-if="selectedProduct && hasExistingImages"
                class="mt-4 grid gap-3 [grid-template-columns:repeat(auto-fill,minmax(5rem,1fr))]"
              >
                <div
                  v-for="image in selectedProduct.attributes?.images || []"
                  :key="image.public_id"
                  class="group relative"
                >
                  <img
                    :src="image.signed_url"
                    :alt="image.alt_text || productDraft.name"
                    class="h-20 w-full rounded-xl object-cover"
                  />
                  <button
                    type="button"
                    class="absolute right-1 top-1 hidden rounded-full bg-n-solid-black/70 p-0.5 text-white group-hover:flex"
                    @click.prevent="deleteImage(image)"
                  >
                    <span class="i-lucide-x h-3 w-3" />
                  </button>
                </div>
              </div>

              <div
                v-else
                class="mt-4 rounded-2xl border border-dashed border-n-weak px-4 py-6 text-center text-sm text-n-slate-11"
              >
                {{
                  selectedProduct
                    ? t('PRODUCTS.FORM.NO_IMAGES')
                    : t('PRODUCTS.FORM.NEW_PRODUCT_IMAGES_HINT')
                }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>

    <!-- Summary modal -->
    <Modal
      v-model:show="isSummaryModalOpen"
      @close="isSummaryModalOpen = false"
    >
      <div class="w-full max-w-[34rem] p-6">
        <h2 class="text-lg font-semibold text-n-slate-12">
          {{ t('PRODUCTS.SUMMARY_TITLE') }}
        </h2>
        <div class="mt-5 grid gap-4 md:grid-cols-3">
          <div class="rounded-2xl border border-n-weak bg-n-alpha-1 p-4">
            <p class="text-xs uppercase tracking-[0.12em] text-n-slate-10">
              {{ t('PRODUCTS.STATS.VISIBLE') }}
            </p>
            <p class="mt-2 text-2xl font-semibold text-n-slate-12">
              {{ visibleProducts }}
            </p>
          </div>
          <div class="rounded-2xl border border-n-weak bg-n-alpha-1 p-4">
            <p class="text-xs uppercase tracking-[0.12em] text-n-slate-10">
              {{ t('PRODUCTS.STATS.ACTIVE') }}
            </p>
            <p class="mt-2 text-2xl font-semibold text-n-slate-12">
              {{ activeProducts }}
            </p>
          </div>
          <div class="rounded-2xl border border-n-weak bg-n-alpha-1 p-4">
            <p class="text-xs uppercase tracking-[0.12em] text-n-slate-10">
              {{ t('PRODUCTS.STATS.AVERAGE_TICKET') }}
            </p>
            <p class="mt-2 text-2xl font-semibold text-n-slate-12">
              {{ formatMoney(averageTicket) }}
            </p>
          </div>
        </div>
      </div>
    </Modal>

    <DeleteModal
      v-model:show="isDeleteModalOpen"
      :title="t('PRODUCTS.DELETE_PRODUCT')"
      :message="t('PRODUCTS.DELETE_CONFIRMATION')"
      :message-value="pendingDeleteProduct?.attributes?.name || ''"
      :confirm-text="t('PRODUCTS.DELETE_PRODUCT')"
      :reject-text="t('PRODUCTS.CANCEL')"
      :on-close="closeDeleteModal"
      :on-confirm="confirmDeleteProduct"
    />

    <!-- Settings modal -->
    <Modal v-model:show="isSettingsModalOpen" :show-close-button="true">
      <div class="w-full max-w-lg p-6">
        <h2 class="mb-1 text-lg font-semibold text-n-slate-12">
          {{ t('PRODUCTS.SETTINGS_TITLE') }}
        </h2>
        <p class="mb-5 text-sm text-n-slate-11">
          {{ t('PRODUCTS.SETTINGS_DESCRIPTION') }}
        </p>
        <label class="mb-1 block text-sm font-medium text-n-slate-12">
          {{ t('PRODUCTS.SHEETS_WEBHOOK_URL') }}
        </label>
        <input
          v-model="sheetsWebhookUrl"
          type="url"
          :placeholder="t('PRODUCTS.SHEETS_WEBHOOK_PLACEHOLDER')"
          class="mb-4 w-full rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2 text-sm text-n-slate-12"
        />
        <div class="flex justify-end gap-2">
          <Button
            color="slate"
            size="sm"
            variant="outline"
            :label="t('PRODUCTS.CANCEL')"
            @click="isSettingsModalOpen = false"
          />
          <Button
            color="blue"
            size="sm"
            :label="t('PRODUCTS.SHEETS_SAVE')"
            :disabled="isSavingWebhook"
            @click="saveWebhookUrl"
          />
        </div>
      </div>
    </Modal>
  </div>
</template>
